import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings, Clock, Plus, Trash2, Save, Calendar, LogOut, CalendarOff, Mail, Tag, Lock,
  FileText, ChevronDown, ChevronUp, Landmark,
} from 'lucide-react';
import { T, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { Glass } from '../components/glass/Glass';
import { SectionHeader } from '../components/cards/SectionHeader';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ─── Schedule Editor ────────────────────────────────────────────────────────

const ScheduleEditor = ({ schedule, onSave, onDelete }) => {
  const [name, setName] = useState(schedule.name);
  const [windows, setWindows] = useState(schedule.config?.windows || []);
  const [saving, setSaving] = useState(false);

  const addWindow = () => {
    setWindows([...windows, { days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' }]);
  };

  const removeWindow = (idx) => {
    setWindows(windows.filter((_, i) => i !== idx));
  };

  const updateWindow = (idx, field, value) => {
    setWindows(windows.map((w, i) => i === idx ? { ...w, [field]: value } : w));
  };

  const toggleDay = (idx, day) => {
    const w = windows[idx];
    const days = w.days.includes(day) ? w.days.filter(d => d !== day) : [...w.days, day].sort();
    updateWindow(idx, 'days', days);
  };

  const save = async () => {
    setSaving(true);
    await supabase.from('schedules').update({ name, config: { windows } }).eq('id', schedule.id);
    setSaving(false);
    if (onSave) onSave();
  };

  return (
    <Glass style={{ padding: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <input value={name} onChange={e => setName(e.target.value)} style={{
          fontSize: 14, fontWeight: 600, border: 'none', background: 'transparent',
          color: T.text, outline: 'none', fontFamily: 'inherit', flex: 1,
        }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {!schedule.is_default && onDelete && (
            <button onClick={onDelete} style={{
              padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(224,88,88,0.20)',
              background: 'transparent', cursor: 'pointer', color: T.red, fontSize: 10,
            }}><Trash2 size={10} /></button>
          )}
          <button onClick={save} disabled={saving} style={{
            padding: '4px 12px', borderRadius: 6, border: 'none',
            background: T.amber, color: '#fff', cursor: 'pointer',
            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Save size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {saving ? 'Saving' : 'Save'}
          </button>
        </div>
      </div>

      {windows.map((w, wi) => (
        <div key={wi} style={{
          padding: '10px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.04)', marginBottom: 8,
        }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            {DAY_NAMES.map((day, di) => (
              <button key={di} onClick={() => toggleDay(wi, di)} style={{
                width: 32, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: w.days.includes(di) ? T.amber : 'rgba(0,0,0,0.04)',
                color: w.days.includes(di) ? '#fff' : T.textLight,
                fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
              }}>{day}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="time" value={w.start} onChange={e => updateWindow(wi, 'start', e.target.value)} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 8px',
              fontSize: 12, fontFamily: 'inherit', background: '#fff', color: T.textMid,
            }} />
            <span style={{ fontSize: 12, color: T.textMuted }}>to</span>
            <input type="time" value={w.end} onChange={e => updateWindow(wi, 'end', e.target.value)} style={{
              border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 8px',
              fontSize: 12, fontFamily: 'inherit', background: '#fff', color: T.textMid,
            }} />
            <div style={{ flex: 1 }} />
            {windows.length > 1 && (
              <button onClick={() => removeWindow(wi)} style={{
                padding: '2px 6px', borderRadius: 4, border: 'none',
                background: 'rgba(224,88,88,0.08)', color: T.red, cursor: 'pointer',
              }}><Trash2 size={10} /></button>
            )}
          </div>
        </div>
      ))}

      <button onClick={addWindow} style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '6px 12px', borderRadius: 6, border: '1px dashed rgba(0,0,0,0.10)',
        background: 'transparent', cursor: 'pointer', fontSize: 11, color: T.textMuted,
        fontFamily: 'inherit', width: '100%', justifyContent: 'center',
      }}>
        <Plus size={10} /> Add time window
      </button>
    </Glass>
  );
};

// ─── Daily Overrides Editor (Addendum Gap 3) ────────────────────────────────

const fmtHM = (mins) => {
  if (typeof mins !== 'number') return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const parseHM = (s) => {
  if (typeof s !== 'string') return 0;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};
const todayIso = () => new Date().toISOString().split('T')[0];

const DailyOverrideRow = ({ override, onSave, onDelete }) => {
  const [date, setDate] = useState(override.date || todayIso());
  const [mode, setMode] = useState(override.mode || 'replace');
  const [hours, setHours] = useState(override.hours || []);
  const [blockRanges, setBlockRanges] = useState(override.block_ranges || []);
  const [reason, setReason] = useState(override.reason || '');
  const [saving, setSaving] = useState(false);

  const list = mode === 'replace' ? hours : blockRanges;
  const setList = mode === 'replace' ? setHours : setBlockRanges;

  const addRange = () => setList([...list, { start_min: 9 * 60, end_min: 17 * 60 }]);
  const removeRange = (i) => setList(list.filter((_, j) => j !== i));
  const updateRange = (i, field, value) => setList(list.map((r, j) => j === i ? { ...r, [field]: parseHM(value) } : r));

  const save = async () => {
    setSaving(true);
    const payload = {
      date, mode, reason: reason || null,
      hours: mode === 'replace' ? hours : [],
      block_ranges: mode === 'block_ranges' ? blockRanges : [],
    };
    await apiFetch('/scheduler/overrides', { method: 'POST', body: JSON.stringify(payload) });
    setSaving(false);
    if (onSave) onSave();
  };

  return (
    <Glass style={{ padding: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{
          border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '4px 8px',
          fontSize: 12, fontFamily: 'inherit', background: '#fff', color: T.text,
        }} />
        <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
          <button onClick={() => setMode('replace')} style={{
            padding: '4px 10px', border: 'none', cursor: 'pointer',
            background: mode === 'replace' ? T.amber : 'rgba(0,0,0,0.02)',
            color: mode === 'replace' ? '#fff' : T.textMid,
            fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
          }}>Replace day</button>
          <button onClick={() => setMode('block_ranges')} style={{
            padding: '4px 10px', border: 'none', cursor: 'pointer',
            background: mode === 'block_ranges' ? T.amber : 'rgba(0,0,0,0.02)',
            color: mode === 'block_ranges' ? '#fff' : T.textMid,
            fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
          }}>Block ranges</button>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={save} disabled={saving} style={{
          padding: '4px 10px', borderRadius: 6, border: 'none',
          background: T.amber, color: '#fff', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
        }}>
          <Save size={10} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          {saving ? 'Saving' : 'Save'}
        </button>
        {onDelete && (
          <button onClick={onDelete} style={{
            padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(224,88,88,0.20)',
            background: 'transparent', cursor: 'pointer', color: T.red,
          }}><Trash2 size={10} /></button>
        )}
      </div>

      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
        {mode === 'replace'
          ? 'Replace the recurring profile for this date. Empty = no work that day.'
          : 'Subtract these ranges from the day\'s working hours (e.g. a personal appointment).'}
      </div>

      {list.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <input type="time" value={fmtHM(r.start_min)} onChange={e => updateRange(i, 'start_min', e.target.value)} style={{
            border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '3px 8px',
            fontSize: 11, fontFamily: 'inherit', background: '#fff', color: T.textMid,
          }} />
          <span style={{ fontSize: 11, color: T.textMuted }}>to</span>
          <input type="time" value={fmtHM(r.end_min)} onChange={e => updateRange(i, 'end_min', e.target.value)} style={{
            border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '3px 8px',
            fontSize: 11, fontFamily: 'inherit', background: '#fff', color: T.textMid,
          }} />
          <div style={{ flex: 1 }} />
          <button onClick={() => removeRange(i)} style={{
            padding: '2px 6px', borderRadius: 4, border: 'none',
            background: 'rgba(224,88,88,0.08)', color: T.red, cursor: 'pointer',
          }}><Trash2 size={10} /></button>
        </div>
      ))}

      <button onClick={addRange} style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 10px', borderRadius: 6, border: '1px dashed rgba(0,0,0,0.10)',
        background: 'transparent', cursor: 'pointer', fontSize: 11, color: T.textMuted,
        fontFamily: 'inherit', marginTop: 4,
      }}>
        <Plus size={10} /> Add range
      </button>

      <input
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder="Reason (optional)"
        style={{
          marginTop: 10, width: '100%', boxSizing: 'border-box',
          border: '1px solid rgba(0,0,0,0.06)', borderRadius: 6, padding: '4px 8px',
          fontSize: 11, fontFamily: 'inherit', background: 'rgba(0,0,0,0.02)', color: T.textMid,
        }}
      />
    </Glass>
  );
};

const DailyOverridesSection = () => {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const start = todayIso();
      const end = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const res = await apiFetch(`/scheduler/overrides?start=${start}&end=${end}`);
      const json = await res.json();
      setOverrides(json.overrides || []);
    } catch (e) {
      setOverrides([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (date) => {
    await apiFetch(`/scheduler/overrides/${date}`, { method: 'DELETE' });
    load();
  };

  return (
    <div style={{ marginTop: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarOff size={16} color={T.amber} />
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Daily overrides</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>One-off changes to a specific day's working hours</span>
        </div>
        <button onClick={() => setAdding(true)} style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 14px', borderRadius: 8, border: 'none',
          background: T.amber, color: '#fff', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
        }}>
          <Plus size={12} /> New override
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {adding && (
          <DailyOverrideRow
            override={{ date: todayIso(), mode: 'block_ranges' }}
            onSave={() => { setAdding(false); load(); }}
            onDelete={() => setAdding(false)}
          />
        )}
        {overrides.map(o => (
          <DailyOverrideRow
            key={o.id}
            override={o}
            onSave={load}
            onDelete={() => remove(o.date)}
          />
        ))}
        {overrides.length === 0 && !adding && !loading && (
          <Glass style={{ padding: 24, textAlign: 'center' }} hover={false}>
            <div style={{ fontSize: 12, color: T.textMuted }}>No overrides set. The recurring profile applies to every day.</div>
          </Glass>
        )}
      </div>
    </div>
  );
};

// ─── Connected Google accounts ──────────────────────────────────────────────

const ConnectedAccountsSection = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    const { data } = await supabase.from('user_google_accounts')
      .select('id, email, scopes, is_active, last_refreshed_at, created_at')
      .order('created_at');
    setAccounts(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Handle the OAuth callback's query params (?google=connected|error). Show
    // a brief notice, then strip them from the URL so a refresh doesn't replay.
    const params = new URLSearchParams(window.location.search);
    const status = params.get('google');
    if (status === 'connected') {
      setNotice({ type: 'ok', msg: `Connected ${params.get('email') || 'Google account'}.` });
    } else if (status === 'error') {
      setNotice({ type: 'err', msg: `Connect failed: ${params.get('reason') || 'unknown'}.` });
    }
    if (status) {
      const url = new URL(window.location.href);
      url.searchParams.delete('google');
      url.searchParams.delete('email');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url);
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await apiFetch('/api/integrations/google/start', { method: 'POST' });
      const { url } = await res.json();
      window.location.href = url;
    } catch (e) {
      setNotice({ type: 'err', msg: 'Could not start Google sign-in.' });
      setConnecting(false);
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this Google account? Any agents using it will stop until it is reconnected.')) return;
    await supabase.from('user_google_accounts').delete().eq('id', id);
    load();
  };

  const summariseScopes = (scopes) => {
    const has = (s) => Array.isArray(scopes) && scopes.some(x => x.includes(s));
    const parts = [];
    if (has('gmail')) parts.push('Gmail');
    if (has('calendar')) parts.push('Calendar');
    return parts.length ? parts.join(' + ') : 'no API access';
  };

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mail size={16} color={T.amber} />
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Connected Google accounts</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>Gmail + Calendar access for triage and calendar sync</span>
        </div>
        <button onClick={connect} disabled={connecting} style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 14px', borderRadius: 8, border: 'none',
          background: T.amber, color: '#fff',
          cursor: connecting ? 'default' : 'pointer',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          opacity: connecting ? 0.6 : 1,
        }}>
          <Plus size={12} /> {connecting ? 'Opening…' : 'Connect Google account'}
        </button>
      </div>

      {notice && (
        <div style={{
          padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 12,
          background: notice.type === 'ok' ? 'rgba(90,158,120,0.10)' : 'rgba(224,88,88,0.10)',
          color: notice.type === 'ok' ? T.green : T.red,
          border: `1px solid ${notice.type === 'ok' ? 'rgba(90,158,120,0.20)' : 'rgba(224,88,88,0.20)'}`,
        }}>
          {notice.msg}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {accounts.map(a => (
          <Glass key={a.id} style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {a.email}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                {summariseScopes(a.scopes)}
                {' · '}
                {a.last_refreshed_at
                  ? `refreshed ${new Date(a.last_refreshed_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                  : 'not yet refreshed'}
              </div>
            </div>
            <button onClick={() => disconnect(a.id)} style={{
              padding: '4px 10px', borderRadius: 6,
              border: '1px solid rgba(224,88,88,0.20)',
              background: 'transparent', color: T.red,
              cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Trash2 size={10} /> Disconnect
            </button>
          </Glass>
        ))}
        {accounts.length === 0 && !loading && (
          <Glass style={{ padding: 24, textAlign: 'center' }} hover={false}>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              No Google accounts connected. Connect one to enable Gmail triage and calendar sync.
            </div>
          </Glass>
        )}
      </div>
    </div>
  );
};

// ─── Connected banks (open banking via GoCardless) ──────────────────────────

const BankAccountsSection = () => {
  const [configured, setConfigured] = useState(true);
  const [connections, setConnections] = useState([]);
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [institutions, setInstitutions] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = async () => {
    try {
      const res = await apiFetch('/api/integrations/bank/connections');
      const data = await res.json();
      setConfigured(data.configured !== false);
      setConnections(data.connections || []);
    } catch { /* backend unreachable — leave empty */ }
    try {
      const res = await apiFetch('/finance/overview');
      const data = await res.json();
      setFinanceAccounts(data.accounts || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const status = params.get('bank');
    if (status === 'connected') {
      setNotice({ type: 'ok', msg: `Connected ${params.get('institution') || 'bank'} (${params.get('accounts') || '?'} account(s)). First sync is running — map each account to its Money-view row below.` });
    } else if (status === 'error') {
      setNotice({ type: 'err', msg: `Bank connect failed: ${params.get('reason') || 'unknown'}.` });
    }
    if (status) {
      const url = new URL(window.location.href);
      ['bank', 'institution', 'accounts', 'reason'].forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url);
    }
  }, []);

  const openPicker = async () => {
    setPickerOpen(o => !o);
    if (!institutions) {
      try {
        const res = await apiFetch('/api/integrations/bank/institutions');
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setInstitutions(data.institutions || []);
      } catch (e) {
        setNotice({ type: 'err', msg: e.message || 'Could not load the bank list.' });
        setPickerOpen(false);
      }
    }
  };

  const connect = async (inst) => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/integrations/bank/start', {
        method: 'POST',
        body: JSON.stringify({ institution_id: inst.id, institution_name: inst.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      window.location.href = data.url;
    } catch (e) {
      setNotice({ type: 'err', msg: `Could not start the bank consent: ${e.message}` });
      setBusy(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      const res = await apiFetch('/api/integrations/bank/sync', { method: 'POST' });
      const s = await res.json();
      if (!res.ok) throw new Error(s.error);
      setNotice({
        type: 'ok',
        msg: `Synced ${s.accountsSynced} account(s): ${s.balancesUpdated} balance(s) updated, ${s.transactionsImported} new transaction(s)${s.inflowsMatched?.length ? `, inflows received: ${s.inflowsMatched.join('; ')}` : ''}${s.errors?.length ? ` — errors: ${s.errors.join('; ')}` : ''}`,
      });
      load();
    } catch (e) {
      setNotice({ type: 'err', msg: `Sync failed: ${e.message}` });
    } finally {
      setSyncing(false);
    }
  };

  const mapAccount = async (id, finance_account_id) => {
    await apiFetch(`/api/integrations/bank/accounts/${id}/map`, {
      method: 'POST', body: JSON.stringify({ finance_account_id: finance_account_id || null }),
    });
    load();
  };

  const disconnect = async (id) => {
    if (!window.confirm('Disconnect this bank? Synced history stays; balances stop updating until reconnected.')) return;
    await apiFetch(`/api/integrations/bank/connections/${id}/delete`, { method: 'POST' });
    load();
  };

  const fmtWhen = (ts) => ts
    ? new Date(ts).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : 'never';

  const shown = (institutions || []).filter(i =>
    !filter.trim() || i.name.toLowerCase().includes(filter.trim().toLowerCase())).slice(0, 12);

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Landmark size={16} color={T.green} />
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Connected banks</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>read-only open banking — balances & transactions for Penny</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {connections.some(c => c.status === 'linked') && (
            <button onClick={syncNow} disabled={syncing} style={{
              padding: '6px 14px', borderRadius: 8, border: `1px solid rgba(90,158,120,0.30)`,
              background: 'transparent', color: T.green,
              cursor: syncing ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            }}>
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          )}
          <button onClick={openPicker} disabled={busy || !configured} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: T.green, color: '#fff',
            cursor: (busy || !configured) ? 'default' : 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            opacity: (busy || !configured) ? 0.6 : 1,
          }}>
            <Plus size={12} /> Connect a bank
          </button>
        </div>
      </div>

      {!configured && (
        <Glass style={{ padding: 14, marginBottom: 10 }} hover={false}>
          <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>
            <strong>Dormant — no UK provider currently available.</strong> As of mid-2026 no
            open-banking aggregator offers self-serve UK account access at personal scale
            (GoCardless closed to new sign-ups; Enable Banking is EU-only; TrueLayer, Salt Edge
            and Plaid need commercial contracts). The integration is built and ready: when a
            provider opens up, its credentials go in the server environment and this section
            comes alive. Until then, statement uploads and Penny's Friday balance check keep
            everything fresh.
          </div>
        </Glass>
      )}

      {notice && (
        <div style={{
          padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 12,
          background: notice.type === 'ok' ? 'rgba(90,158,120,0.10)' : 'rgba(224,88,88,0.10)',
          color: notice.type === 'ok' ? T.green : T.red,
          border: `1px solid ${notice.type === 'ok' ? 'rgba(90,158,120,0.20)' : 'rgba(224,88,88,0.20)'}`,
        }}>
          {notice.msg}
        </div>
      )}

      {pickerOpen && (
        <Glass style={{ padding: 14, marginBottom: 10 }} hover={false}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search banks — Barclays, Revolut, Vanquis… (Sandbox Finance is the test bank)"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '8px 12px', marginBottom: 8,
              borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
              background: 'rgba(255,255,255,0.85)', fontSize: 13, fontFamily: 'inherit',
              color: T.text, outline: 'none',
            }}
          />
          {institutions === null ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: 8 }}>Loading bank list…</div>
          ) : shown.map(inst => (
            <button key={inst.id} onClick={() => connect(inst)} disabled={busy} style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
              padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'transparent', fontFamily: 'inherit',
            }}>
              {inst.logo && <img src={inst.logo} alt="" width={20} height={20} style={{ borderRadius: 4 }} />}
              <span style={{ fontSize: 13, color: T.text }}>{inst.name}</span>
            </button>
          ))}
        </Glass>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {connections.filter(c => c.status !== 'revoked').map(c => (
          <Glass key={c.id} style={{ padding: 14 }} hover={false}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: c.accounts?.length ? 10 : 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.institution_name}</span>
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                  padding: '2px 7px', borderRadius: 8,
                  background: c.status === 'linked' ? 'rgba(90,158,120,0.10)' : 'rgba(212,146,30,0.10)',
                  color: c.status === 'linked' ? T.green : T.amber,
                }}>{c.status}</span>
              </div>
              <button onClick={() => disconnect(c.id)} style={{
                padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(224,88,88,0.20)',
                background: 'transparent', color: T.red, cursor: 'pointer',
                fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Trash2 size={10} /> Disconnect
              </button>
            </div>
            {(c.accounts || []).map(a => (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                padding: '8px 10px', borderRadius: 8, background: 'rgba(0,0,0,0.02)',
                border: '1px solid rgba(0,0,0,0.04)', marginBottom: 6,
              }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.text }}>{a.display_name}</div>
                  <div style={{ fontSize: 10.5, color: T.textMuted }}>
                    {a.last_balance != null ? `£${Number(a.last_balance).toLocaleString('en-GB', { minimumFractionDigits: 2 })} · ` : ''}
                    synced {fmtWhen(a.last_synced_at)}
                  </div>
                </div>
                <select
                  value={a.finance_account_id || ''}
                  onChange={e => mapAccount(a.id, e.target.value)}
                  style={{
                    padding: '6px 8px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.10)',
                    background: '#fff', fontSize: 11.5, fontFamily: 'inherit', color: T.textMid, maxWidth: 240,
                  }}
                >
                  <option value="">— transactions only (no balance mapping) —</option>
                  {financeAccounts.map(f => (
                    <option key={f.id} value={f.id}>{f.name} ({f.kind})</option>
                  ))}
                </select>
              </div>
            ))}
          </Glass>
        ))}
        {configured && connections.filter(c => c.status !== 'revoked').length === 0 && (
          <Glass style={{ padding: 24, textAlign: 'center' }} hover={false}>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              No banks connected. Connect one and map each account to its Money-view row —
              balances and transactions then keep themselves fresh for Penny.
            </div>
          </Glass>
        )}
      </div>
    </div>
  );
};

// ─── Triage labels (per connected Gmail account) ────────────────────────────

const TriageLabelsSection = () => {
  // Pulls each Gmail-scoped account and its triage_labels. Standards are
  // shown read-only (the trigger seeds them on Connect and the runner relies
  // on the 8 canonical names); customs are user-managed inline.
  const [accounts, setAccounts] = useState([]);
  const [labelsByAccount, setLabelsByAccount] = useState({});
  const [loading, setLoading] = useState(true);
  const [draftByAccount, setDraftByAccount] = useState({});

  const load = async () => {
    const { data: accs } = await supabase.from('user_google_accounts')
      .select('id, email, scopes')
      .order('created_at');
    const gmailAccs = (accs || []).filter(a =>
      Array.isArray(a.scopes) && a.scopes.some(s => s.includes('gmail'))
    );
    setAccounts(gmailAccs);

    if (gmailAccs.length === 0) {
      setLabelsByAccount({});
      setLoading(false);
      return;
    }
    const ids = gmailAccs.map(a => a.id);
    const { data: labels } = await supabase.from('triage_labels')
      .select('id, account_id, name, description, keeps_in_inbox, is_standard, sort_order')
      .in('account_id', ids)
      .order('sort_order');
    const grouped = {};
    for (const id of ids) grouped[id] = [];
    for (const l of labels || []) (grouped[l.account_id] ||= []).push(l);
    setLabelsByAccount(grouped);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startDraft = (accountId) => {
    setDraftByAccount(d => ({ ...d, [accountId]: { name: '', description: '', keeps_in_inbox: false } }));
  };

  const cancelDraft = (accountId) => {
    setDraftByAccount(d => { const next = { ...d }; delete next[accountId]; return next; });
  };

  const saveDraft = async (accountId) => {
    const draft = draftByAccount[accountId];
    if (!draft?.name?.trim()) return;
    // Next sort_order = (max existing) + 1, so customs trail the 8 standards.
    const existing = labelsByAccount[accountId] || [];
    const nextSort = (existing.reduce((m, l) => Math.max(m, l.sort_order || 0), 0)) + 1;
    const { error } = await supabase.from('triage_labels').insert({
      account_id: accountId,
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      keeps_in_inbox: !!draft.keeps_in_inbox,
      is_standard: false,
      sort_order: nextSort,
    });
    if (error) {
      window.alert(`Could not add label: ${error.message}`);
      return;
    }
    cancelDraft(accountId);
    load();
  };

  const deleteLabel = async (label) => {
    if (label.is_standard) return; // UI guards; standards are seeded.
    if (!window.confirm(`Delete custom label "${label.name}"? Threads already labeled in Gmail keep the label — only future triage stops using it.`)) return;
    await supabase.from('triage_labels').delete().eq('id', label.id);
    load();
  };

  if (loading) return null;
  if (accounts.length === 0) return null; // Nothing to show until a Gmail account is connected.

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Tag size={16} color={T.amber} />
        <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Triage labels</span>
        <span style={{ fontSize: 11, color: T.textMuted }}>Standards are seeded automatically. Add your own custom labels per account.</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {accounts.map(acc => {
          const labels = labelsByAccount[acc.id] || [];
          const standards = labels.filter(l => l.is_standard);
          const customs   = labels.filter(l => !l.is_standard);
          const draft = draftByAccount[acc.id];
          return (
            <Glass key={acc.id} style={{ padding: 16 }} hover={false}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>{acc.email}</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {standards.map(l => (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', borderRadius: 6,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <Lock size={10} color={T.textMuted} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{l.name}</span>
                    <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 'auto' }}>
                      {l.keeps_in_inbox ? 'stays in inbox' : 'filed out'} · standard
                    </span>
                  </div>
                ))}
                {customs.map(l => (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', borderRadius: 6,
                    background: 'rgba(218,165,32,0.04)',
                    border: '1px solid rgba(218,165,32,0.10)',
                  }}>
                    <Tag size={10} color={T.amber} />
                    <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{l.name}</span>
                    <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 'auto' }}>
                      {l.keeps_in_inbox ? 'stays in inbox' : 'filed out'} · custom
                    </span>
                    <button onClick={() => deleteLabel(l)} style={{
                      padding: 4, borderRadius: 4, border: 'none', background: 'transparent',
                      color: T.red, cursor: 'pointer', display: 'flex', alignItems: 'center',
                    }} title="Delete custom label">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>

              {draft ? (
                <div style={{ marginTop: 10, padding: 12, borderRadius: 8, background: 'rgba(74,158,204,0.04)', border: '1px solid rgba(74,158,204,0.10)' }}>
                  <input
                    type="text" placeholder="Label name"
                    value={draft.name}
                    onChange={e => setDraftByAccount(d => ({ ...d, [acc.id]: { ...d[acc.id], name: e.target.value } }))}
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(0,0,0,0.20)',
                      color: T.text, fontSize: 13, fontFamily: 'inherit', marginBottom: 6,
                    }}
                  />
                  <textarea
                    placeholder="Description (helps the triage agent decide when this label fits)"
                    rows={2}
                    value={draft.description}
                    onChange={e => setDraftByAccount(d => ({ ...d, [acc.id]: { ...d[acc.id], description: e.target.value } }))}
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(0,0,0,0.20)',
                      color: T.text, fontSize: 12, fontFamily: 'inherit', marginBottom: 6, resize: 'vertical',
                    }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMuted, marginBottom: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!draft.keeps_in_inbox}
                      onChange={e => setDraftByAccount(d => ({ ...d, [acc.id]: { ...d[acc.id], keeps_in_inbox: e.target.checked } }))}
                    />
                    Keep labeled threads in the inbox (otherwise they're filed out)
                  </label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => saveDraft(acc.id)} disabled={!draft.name.trim()} style={{
                      padding: '5px 12px', borderRadius: 6, border: 'none',
                      background: T.amber, color: '#fff',
                      cursor: draft.name.trim() ? 'pointer' : 'default',
                      fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                      opacity: draft.name.trim() ? 1 : 0.5,
                    }}>Save</button>
                    <button onClick={() => cancelDraft(acc.id)} style={{
                      padding: '5px 12px', borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.10)',
                      background: 'transparent', color: T.textMuted,
                      cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => startDraft(acc.id)} style={{
                  marginTop: 10, padding: '5px 12px', borderRadius: 6,
                  border: '1px dashed rgba(255,255,255,0.15)',
                  background: 'transparent', color: T.textMuted,
                  cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <Plus size={11} /> Add custom label
                </button>
              )}
            </Glass>
          );
        })}
      </div>
    </div>
  );
};

// ─── Agent context (editable per-agent context) ────────────────────────────

const CONTEXT_SCOPES = [
  { scope: 'shared',        label: 'Shared — all agents',       hint: 'The base every agent sees: who you are, your ventures, current priorities. Edit this to update all agents at once.' },
  { scope: 'saul',          label: 'Saul — Chief of Staff',     hint: 'Extra context just for Saul (added on top of Shared).' },
  { scope: 'kay',           label: 'Kay — Executive Assistant', hint: 'Extra context just for Kay (added on top of Shared).' },
  { scope: 'penny',         label: 'Penny — Financial Planner', hint: 'Extra context just for Penny (added on top of Shared).' },
  { scope: 'morning-brief', label: 'Morning brief agent',       hint: 'Extra context for the morning brief.' },
  { scope: 'prioritise',    label: 'Prioritisation agent',      hint: 'Extra context for the prioritisation review.' },
  { scope: 'liken-insight', label: 'Liken insight agent',       hint: 'Extra context for the Liken insight brief.' },
  { scope: 'email-agent',   label: 'Email agent',               hint: 'Extra context for the email agent.' },
  { scope: 'research',      label: 'Research agent',            hint: 'Extra context for research briefs.' },
];

const AgentContextRow = ({ def, value, dirty, saved, saving, expanded, onToggle, onChange, onSave }) => (
  <Glass style={{ padding: 0, overflow: 'hidden' }} hover={false}>
    <button onClick={onToggle} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
      background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
    }}>
      <FileText size={14} color={T.amber} />
      <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{def.label}</span>
      <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 8 }}>
        {value && value.trim() ? `${value.trim().length} chars` : 'empty'}{dirty ? ' · unsaved' : ''}
      </span>
      <span style={{ marginLeft: 'auto', display: 'flex' }}>
        {expanded ? <ChevronUp size={14} color={T.textMuted} /> : <ChevronDown size={14} color={T.textMuted} />}
      </span>
    </button>
    {expanded && (
      <div style={{ padding: '0 16px 16px' }}>
        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>{def.hint}</div>
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={def.scope === 'shared' ? 12 : 5}
          placeholder={def.scope === 'shared'
            ? 'Who you are, your ventures, what matters right now…'
            : 'Optional extra context just for this agent…'}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px',
            borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.5,
            fontFamily: 'inherit', color: T.text, outline: 'none', resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
          <button onClick={onSave} disabled={saving || !dirty} style={{
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: dirty ? T.amber : 'rgba(0,0,0,0.06)', color: dirty ? '#fff' : T.textMuted,
            cursor: (dirty && !saving) ? 'pointer' : 'default',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span style={{ fontSize: 12, color: T.green || '#34a853' }}>Saved ✓</span>}
        </div>
      </div>
    )}
  </Glass>
);

const AgentContextSection = () => {
  const [contexts, setContexts] = useState({});
  const [drafts, setDrafts] = useState({});
  const [open, setOpen] = useState({ shared: true });
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);

  const load = async () => {
    try {
      const res = await apiFetch('/ea/context');
      const json = await res.json();
      setContexts(json.contexts || {});
    } catch { setContexts({}); }
  };
  useEffect(() => { load(); }, []);

  const valueFor = (scope) => drafts[scope] !== undefined ? drafts[scope] : (contexts[scope]?.content || '');
  const dirtyFor = (scope) => drafts[scope] !== undefined && drafts[scope] !== (contexts[scope]?.content || '');

  const save = async (scope) => {
    setSaving(scope);
    try {
      await apiFetch('/ea/context', { method: 'PUT', body: JSON.stringify({ scope, content: valueFor(scope) }) });
      await load();
      setDrafts(d => { const n = { ...d }; delete n[scope]; return n; });
      setSaved(scope);
      setTimeout(() => setSaved(s => (s === scope ? null : s)), 2500);
    } finally { setSaving(null); }
  };

  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <FileText size={16} color={T.amber} />
        <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Agent context</span>
        <span style={{ fontSize: 11, color: T.textMuted }}>What each agent knows about you — edit live, no redeploy</span>
      </div>
      <div style={{ fontSize: 12, color: T.textMid, marginBottom: 14, lineHeight: 1.5 }}>
        Every agent reads the <strong>Shared</strong> context first, then layers its own on top. Edit Shared to update them all at once.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {CONTEXT_SCOPES.map(def => (
          <AgentContextRow
            key={def.scope}
            def={def}
            value={valueFor(def.scope)}
            dirty={dirtyFor(def.scope)}
            saved={saved === def.scope}
            saving={saving === def.scope}
            expanded={!!open[def.scope]}
            onToggle={() => setOpen(o => ({ ...o, [def.scope]: !o[def.scope] }))}
            onChange={(v) => setDrafts(d => ({ ...d, [def.scope]: v }))}
            onSave={() => save(def.scope)}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main SettingsView ──────────────────────────────────────────────────────

export const SettingsView = ({ onUpdate, onLogout }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSchedules = async () => {
    const { data } = await supabase.from('schedules').select('*').order('created_at');
    setSchedules(data || []);
    setLoading(false);
  };

  useEffect(() => { loadSchedules(); }, []);

  const createSchedule = async () => {
    await supabase.from('schedules').insert({
      name: 'New Schedule',
      config: { windows: [{ days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00' }] },
    });
    loadSchedules();
  };

  const deleteSchedule = async (id) => {
    await supabase.from('schedules').delete().eq('id', id);
    loadSchedules();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Settings</h1>
        <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>Manage schedules and working hours</p>
      </div>

      <div style={{ maxWidth: 600 }}>
        {/* Connected Google accounts */}
        <ConnectedAccountsSection />

        <BankAccountsSection />

        {/* Triage labels per account */}
        <TriageLabelsSection />

        {/* Editable per-agent context */}
        <AgentContextSection />

        {/* Schedules section */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={16} color={T.amber} />
            <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Schedules</span>
            <span style={{ fontSize: 11, color: T.textMuted }}>Define when the scheduler can place tasks</span>
          </div>
          <button onClick={createSchedule} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: T.amber, color: '#fff', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          }}>
            <Plus size={12} /> New Schedule
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {schedules.map(s => (
            <ScheduleEditor
              key={s.id}
              schedule={s}
              onSave={loadSchedules}
              onDelete={s.is_default ? null : () => deleteSchedule(s.id)}
            />
          ))}
          {schedules.length === 0 && !loading && (
            <Glass style={{ padding: 32, textAlign: 'center' }} hover={false}>
              <div style={{ fontSize: 13, color: T.textMuted }}>No schedules defined. Create one to get started.</div>
            </Glass>
          )}
        </div>

        <div style={{ marginTop: 24, padding: '14px 18px', borderRadius: 10, background: 'rgba(74,158,204,0.05)', border: '1px solid rgba(74,158,204,0.10)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.blue, marginBottom: 6 }}>How schedules work</div>
          <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.6 }}>
            Each schedule defines time windows when the auto-scheduler can place tasks.
            Assign a schedule to a task in the task editor. Tasks default to "Work Hours" if no schedule is set.
            The scheduler respects these windows — a task assigned to "Personal" won't be placed during work hours.
          </div>
        </div>

        <DailyOverridesSection />

        {onLogout && (
          <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <button onClick={onLogout} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 10, border: 'none',
              background: 'rgba(0,0,0,0.04)', color: T.textMid, cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            }}>
              <LogOut size={12} /> Sign out
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
